import queryClient from '@src/app/providers/queryClient';
import type { UseQueryParams } from '@src/features/types';
import { branchSearchFromDto } from '@src/features/users/lib/branch.ts';
import { departmentFromDto } from '@src/features/users/lib/department.ts';
import { employmentFromDto } from '@src/features/users/lib/employment.ts';
import { jobTitleFromDto } from '@src/features/users/lib/jobtitle';
import { rankFromDto } from '@src/features/users/lib/rank.ts';
import {
  salaryFromDto,
  salaryTimelineResponseFromDto,
} from '@src/features/users/lib/salary';
import { teamFromDto } from '@src/features/users/lib/team';
import { titleFromDto } from '@src/features/users/lib/title';
import {
  shortUserFromDto,
  userDetailFromDto,
  userListFromDto,
  userSharedDetailFromDto,
} from '@src/features/users/lib/user';
import {
  createUserWarningDefinitionsLoader,
  validateUserWarningDefinitions,
} from '@src/features/users/lib/userWarnings';
import {
  vacationCreditCommitResponseFromDto,
  vacationCreditFromDto,
} from '@src/features/users/lib/vacationCredit';
import type {
  BranchSearchResponse,
  BranchSearchResponseDto,
  CountryOptionsResponse,
  DepartmentResponse,
  DepartmentResponseDto,
  EmploymentResponse,
  EmploymentResponseDto,
  JobTitleResponse,
  JobTitleResponseDto,
  RankResponse,
  RankResponseDto,
  Salary,
  SalaryTimelineResponse,
  SalaryTimelineResponseDto,
  SalaryWritePayload,
  StatusChoicesResponse,
  TeamResponse,
  TeamResponseDto,
  TitleResponse,
  TitleResponseDto,
  UserDetail,
  UserDetailDto,
  UserList,
  UserPermission,
  UserScheduleResponseDto,
  UserScheduleUpdatePayload,
  UserSearchResponse,
  UserSearchResponseDto,
  UserSharedDetail,
  UserSharedDetailDto,
  UsersResponse,
  UserUpdateDto,
  UserWarningDefinitionsDto,
  UserWarningEvaluationDto,
  UserWarningPatchDto,
  UserWarningPatchEvaluatePayload,
  UseUsersParams,
  VacationCredit,
  VacationCreditCommitResponse,
  VacationCreditCommitResponseDto,
  VacationCreditDto,
  VacationCreditKnownPayload,
  VacationCreditStatePayload,
} from '@src/features/users/types';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { buildSectionCommitFormData, formatValue } from '@src/shared/lib';
import { serialize } from '@src/shared/lib/formUtils';
import { useMutation, useQuery } from '@src/shared/query';
import { urlToQueryKey } from '@src/shared/query/utils';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { usersUrl } from './constants';
import { invalidateUserQualificationsQuery } from './useQualifications';

export type CreateUserPayload = UserUpdateDto;
export type CreateUserResponse = Record<string, unknown> & {
  id: number;
  salary_id: number | null;
  warning_evaluation: UserWarningEvaluationDto;
};
export type UpdateUserPayload = {
  id: number | string;
  payload: Partial<UserUpdateDto>;
};
export type UpdateUserResponse = Record<string, unknown>;
export type CommitUserProfilePayload = Record<string, Record<string, unknown>>;
export type CommitUserProfileResponse = {
  results: Record<string, unknown>;
  warning_evaluation: UserWarningEvaluationDto;
};
export type UsersListResponse = Omit<UsersResponse, 'results'> & {
  results: UserList[];
};

export function invalidateUserByIdQuery(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: ['root', ...urlToQueryKey(`users/${String(id)}/`)],
  });
}

export function invalidateUserScheduleQuery(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: ['root', ...urlToQueryKey(`users/${String(id)}/schedule/`)],
  });
}

export function invalidateUserSalariesQuery(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: ['root', ...urlToQueryKey(`users/${String(id)}/salaries/`)],
  });
}

export function invalidateUserVacationCreditsQuery(id: number | string) {
  return queryClient.invalidateQueries({
    queryKey: [
      'root',
      ...urlToQueryKey(`users/${String(id)}/vacation-credits/`),
    ],
  });
}

export function useUsers({ pageSize, ordering, filterParams }: UseUsersParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: ['root', 'users', 'admin-grid', pageSize, ordering, filterParams],
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: usersUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<UsersResponse>,
    select: (data): InfiniteData<UsersListResponse> => ({
      ...data,
      pages: (data.pages ?? []).map((page) => ({
        ...page,
        results: page.results.map(userListFromDto),
      })),
    }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
  });
}

export function useCreateUser() {
  return useMutation<CreateUserResponse, Error, CreateUserPayload>({
    mutationKey: ['root', 'users', 'create'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: usersUrl,
        method: 'POST',
        payload: serialize(payload),
        contentType: 'multipart/form-data',
      }) as Promise<CreateUserResponse>,
  });
}

export function useUpdateUser() {
  return useMutation<UpdateUserResponse, Error, UpdateUserPayload>({
    mutationKey: ['root', 'users', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `${usersUrl}${id}/`,
        method: 'PATCH',
        payload: serialize(payload),
        contentType: 'multipart/form-data',
      }) as Promise<UpdateUserResponse>,
    onSuccess: (_response, variables) => invalidateUserByIdQuery(variables.id),
  });
}

export function useEvaluateUserWarnings() {
  return useMutation<
    UserWarningPatchDto,
    Error,
    UserWarningPatchEvaluatePayload
  >({
    mutationKey: ['root', 'users', 'warnings', 'evaluate'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: `${usersUrl}warnings/evaluate/`,
        method: 'POST',
        payload,
      }) as Promise<UserWarningPatchDto>,
  });
}

// `sectionByField` is the map of the page that shows the warnings: the
// definitions are checked against it, so a field the page cannot place is
// reported. It has to be a stable reference.
export function useUserWarningDefinitions(
  sectionByField: Readonly<Record<string, string>>,
) {
  const select = useCallback(
    (payload: UserWarningDefinitionsDto) =>
      validateUserWarningDefinitions(payload, { sectionByField }),
    [sectionByField],
  );
  const { data, refetch } = useQuery<UserWarningDefinitionsDto>(
    `${usersUrl}warnings/definitions/`,
    {
      select,
      staleTime: Number.POSITIVE_INFINITY,
      // A failed request is not repeated automatically: `load` below asks
      // again when a check needs the definitions, so the user learns about
      // the failure at once instead of after several silent attempts.
      retry: false,
    },
  );
  const dataRef = useRef(data);
  dataRef.current = data;
  const loader = useMemo(
    () =>
      createUserWarningDefinitionsLoader({
        // Joins a request that is already running instead of restarting it.
        fetch: async () => (await refetch({ cancelRefetch: false })).data,
      }),
    [refetch],
  );
  // Returns the definitions, requesting them if they are missing.
  const load = useCallback(() => loader(dataRef.current), [loader]);

  return { data, load, refetch };
}

export function useCommitUserProfile() {
  return useMutation<
    CommitUserProfileResponse,
    Error,
    { id: number | string; sections: CommitUserProfilePayload }
  >({
    mutationKey: ['root', 'users', 'profile', 'commit'],
    mutateFn: ({ apiClient, id, sections }) =>
      apiClient.request({
        url: `${usersUrl}${String(id)}/profile/commit/`,
        method: 'PATCH',
        payload: buildSectionCommitFormData(sections),
        contentType: 'multipart/form-data',
      }) as Promise<CommitUserProfileResponse>,
    onSuccess: (_response, variables) => {
      invalidateUserByIdQuery(variables.id);
      invalidateUserScheduleQuery(variables.id);
      invalidateUserSalariesQuery(variables.id);
      invalidateUserQualificationsQuery(variables.id);
      invalidateUserVacationCreditsQuery(variables.id);
    },
  });
}

export function useUserById(
  id: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<UserDetailDto, UserDetail>(`users/${String(id ?? '')}/`, {
    enabled: Boolean(id) && enabled,
    select: (data): UserDetail => userDetailFromDto(data),
  });
}

export function useUserSchedule(
  id: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<UserScheduleResponseDto>(
    `users/${String(id ?? '')}/schedule/`,
    {
      enabled: Boolean(id) && enabled,
    },
  );
}

export function useApproveUserSchedule() {
  return useMutation<
    UserScheduleResponseDto['future_schedule'],
    Error,
    {
      id: number | string;
      scheduleId: number | string;
      scheduleVersion: number;
      confirmPast?: boolean;
    }
  >({
    mutationKey: ['root', 'users', 'schedule', 'approve'],
    mutateFn: ({
      apiClient,
      id,
      scheduleId,
      scheduleVersion,
      confirmPast = false,
    }) =>
      apiClient.request({
        url: `users/${String(id)}/schedule/approve/`,
        method: 'POST',
        payload: {
          id: scheduleId,
          version: scheduleVersion,
          confirm_past: confirmPast,
        },
      }) as Promise<UserScheduleResponseDto['future_schedule']>,
    onSuccess: (_response, variables) =>
      invalidateUserScheduleQuery(variables.id),
    onError: (error, variables) => {
      const data = (
        error as {
          response?: { data?: Partial<UserScheduleResponseDto> };
        }
      ).response?.data;

      if (data && 'future_schedule' in data) {
        queryClient.setQueryData<UserScheduleResponseDto>(
          ['root', ...urlToQueryKey(`users/${String(variables.id)}/schedule/`)],
          (current) =>
            current
              ? { ...current, future_schedule: data.future_schedule ?? null }
              : current,
        );
      }
    },
  });
}

export function useUpdateUserSchedule() {
  return useMutation<
    UserScheduleResponseDto['future_schedule'],
    Error,
    { id: number | string; payload: UserScheduleUpdatePayload }
  >({
    mutationKey: ['root', 'users', 'schedule', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `users/${String(id)}/schedule/`,
        method: 'POST',
        payload,
      }) as Promise<UserScheduleResponseDto['future_schedule']>,
    onSuccess: (_response, variables) =>
      invalidateUserScheduleQuery(variables.id),
  });
}

export function useResetUserSchedule() {
  return useMutation<UserScheduleResponseDto, Error, { id: number | string }>({
    mutationKey: ['root', 'users', 'schedule', 'reset'],
    mutateFn: ({ apiClient, id }) =>
      apiClient.request({
        url: `users/${String(id)}/schedule/`,
        method: 'DELETE',
      }) as Promise<UserScheduleResponseDto>,
    onSuccess: (_response, variables) =>
      invalidateUserScheduleQuery(variables.id),
  });
}

export function useUserSalaries(
  id: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<
    {
      results: import('@src/features/users/types').SalaryDto[];
      currency?: string;
    },
    { results: Salary[]; currency: string }
  >(`users/${String(id ?? '')}/salaries/`, {
    enabled: Boolean(id) && enabled,
    select: (data) => ({
      results: data.results.map(salaryFromDto),
      currency: data.currency ?? 'CHF',
    }),
  });
}

export function useBatchCreateUserSalaries() {
  return useMutation<
    SalaryTimelineResponse,
    Error,
    {
      id: number | string;
      confirmed: boolean;
      salaries: SalaryWritePayload[];
    }
  >({
    mutationKey: ['root', 'users', 'salaries', 'batch-create'],
    mutateFn: ({ apiClient, id, confirmed, salaries }) =>
      apiClient
        .request({
          url: `users/${String(id)}/salaries/`,
          method: 'POST',
          payload: { confirmed, salaries },
        })
        .then((response) =>
          salaryTimelineResponseFromDto(response as SalaryTimelineResponseDto),
        ),
    onSuccess: (_response, variables) => {
      invalidateUserSalariesQuery(variables.id);
      invalidateUserByIdQuery(variables.id);
      // Saving a salary regenerates the yearly (Jan 1) vacation credits
      // server-side, so the credits section would otherwise show stale days.
      invalidateUserVacationCreditsQuery(variables.id);
    },
  });
}

export function useUpdateUserSalary() {
  return useMutation<
    SalaryTimelineResponse,
    Error,
    {
      id: number | string;
      salaryId: number | string;
      confirmed: boolean;
      salary: SalaryWritePayload;
    }
  >({
    mutationKey: ['root', 'users', 'salaries', 'update'],
    mutateFn: ({ apiClient, id, salaryId, confirmed, salary }) =>
      apiClient
        .request({
          url: `users/${String(id)}/salaries/${String(salaryId)}/`,
          method: 'PATCH',
          payload: { ...salary, confirmed },
        })
        .then((response) =>
          salaryTimelineResponseFromDto(response as SalaryTimelineResponseDto),
        ),
    onSuccess: (_response, variables) => {
      invalidateUserSalariesQuery(variables.id);
      invalidateUserByIdQuery(variables.id);
      // Saving a salary regenerates the yearly (Jan 1) vacation credits
      // server-side, so the credits section would otherwise show stale days.
      invalidateUserVacationCreditsQuery(variables.id);
    },
  });
}

export function useDryRunUserSalaryTimeline() {
  return useMutation<
    SalaryTimelineResponse,
    Error,
    {
      id: number | string;
      row: import('@src/features/users/types').SalaryTimelineStatePayload;
      stateUi: import('@src/features/users/types').SalaryTimelineStatePayload[];
    }
  >({
    mutationKey: ['root', 'users', 'salaries', 'dry-run'],
    mutateFn: ({ apiClient, id, row, stateUi }) =>
      apiClient
        .request({
          url: `users/${String(id)}/salaries/dry-run/`,
          method: 'POST',
          payload: { row, state_ui: stateUi },
        })
        .then((response) =>
          salaryTimelineResponseFromDto(response as SalaryTimelineResponseDto),
        ),
  });
}

export function useCommitUserSalaryTimeline() {
  return useMutation<
    SalaryTimelineResponse,
    Error,
    {
      id: number | string;
      payload: import('@src/features/users/types').SalaryTimelineCommitPayload;
    }
  >({
    mutationKey: ['root', 'users', 'salaries', 'commit'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient
        .request({
          url: `users/${String(id)}/salaries/commit/`,
          method: 'POST',
          payload,
        })
        .then((response) =>
          salaryTimelineResponseFromDto(response as SalaryTimelineResponseDto),
        ),
    onSuccess: (_response, variables) => {
      invalidateUserSalariesQuery(variables.id);
      invalidateUserByIdQuery(variables.id);
      // Saving a salary regenerates the yearly (Jan 1) vacation credits
      // server-side, so the credits section would otherwise show stale days.
      invalidateUserVacationCreditsQuery(variables.id);
    },
  });
}

export function useUserVacationCredits(
  id: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<
    { results: VacationCreditDto[] },
    { results: VacationCredit[] }
  >(`users/${String(id ?? '')}/vacation-credits/`, {
    enabled: Boolean(id) && enabled,
    select: (data) => ({
      results: data.results.map(vacationCreditFromDto),
    }),
  });
}

export function useCommitUserVacationCredits() {
  return useMutation<
    VacationCreditCommitResponse,
    Error,
    {
      id: number | string;
      confirmed: boolean;
      known: VacationCreditKnownPayload[];
      stateUi: VacationCreditStatePayload[];
    }
  >({
    mutationKey: ['root', 'users', 'vacation-credits', 'commit'],
    mutateFn: ({ apiClient, id, confirmed, known, stateUi }) =>
      apiClient
        .request({
          url: `users/${String(id)}/vacation-credits/commit/`,
          method: 'POST',
          payload: { confirmed, known, state_ui: stateUi },
        })
        .then((response) =>
          vacationCreditCommitResponseFromDto(
            response as VacationCreditCommitResponseDto,
          ),
        ),
    onSuccess: (_response, variables) => {
      invalidateUserVacationCreditsQuery(variables.id);
    },
  });
}

export function useUserSharedById(
  id: number | string | null | undefined,
  enabled = true,
) {
  return useQuery<UserSharedDetailDto, UserSharedDetail>(
    `users/${String(id ?? '')}/shared/`,
    {
      enabled: Boolean(id) && enabled,
      select: (data): UserSharedDetail => userSharedDetailFromDto(data),
    },
  );
}

export function useUserPermissions() {
  return useQuery<UserPermission[]>('users/permissions/', {
    initialData: [],
  });
}

export type UserStatsDto = {
  online: number;
  total: number;
  birthdays_today: number;
};

export type UserStats = {
  online: number;
  total: number;
  birthdaysToday: number;
};

export function useUserStats() {
  return useQuery<UserStatsDto, UserStats>('users/stats/', {
    select: (data): UserStats => ({
      online: data.online,
      total: data.total,
      birthdaysToday: data.birthdays_today,
    }),
  });
}

export function useUserStatusChoices() {
  return useQuery<StatusChoicesResponse>('users/choices/user-statuses/', {
    initialData: {},
  });
}

export function useUserBranchChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<BranchSearchResponseDto, BranchSearchResponse>(
    'users/branch/search/',
    {
      queryParams,
      enabled,
      select: (data): BranchSearchResponse => ({
        ...data,
        results: data.results.map(branchSearchFromDto),
      }),
    },
  );
}

export function useCountryChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<CountryOptionsResponse>('locales/country/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): CountryOptionsResponse => ({
      ...data,
      results: [...data.results]
        .filter((country) => formatValue(country.ctry_name, ''))
        .sort((a, b) =>
          formatValue(a.ctry_name, '').localeCompare(
            formatValue(b.ctry_name, ''),
          ),
        ),
    }),
  });
}

export function useUserSearchChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<UserSearchResponseDto, UserSearchResponse>('users/search/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): UserSearchResponse => ({
      ...data,
      results: data.results.map(shortUserFromDto),
    }),
  });
}

export function useUserAutocompleteChoices(
  params: Partial<UseQueryParams> = {},
) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<UserSearchResponseDto, UserSearchResponse>(
    'users/autocomplete/',
    {
      queryParams,
      enabled,
      select: (data): UserSearchResponse => ({
        ...data,
        results: data.results.map(shortUserFromDto),
      }),
    },
  );
}

export function useUserRankChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<RankResponseDto, RankResponse>('users/rank/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): RankResponse => ({
      ...data,
      results: data.results.map(rankFromDto),
    }),
  });
}

export function useUserTitleChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<TitleResponseDto, TitleResponse>('users/title/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): TitleResponse => ({
      ...data,
      results: data.results.map(titleFromDto),
    }),
  });
}

export function useUserJobTitleAutocompleteChoices(
  params: Partial<UseQueryParams> = {},
) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<JobTitleResponseDto, JobTitleResponse>(
    'users/job-title/autocomplete/',
    {
      queryParams,
      enabled,
      initialData: { results: [] },
      select: (data): JobTitleResponse => ({
        ...data,
        results: data.results.map(jobTitleFromDto),
      }),
    },
  );
}

export function useUserTeamChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<TeamResponseDto, TeamResponse>('users/team/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): TeamResponse => ({
      ...data,
      results: data.results.map(teamFromDto),
    }),
  });
}

export function useUserDepartmentChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<DepartmentResponseDto, DepartmentResponse>(
    'users/department/',
    {
      queryParams,
      enabled,
      initialData: { results: [] },
      select: (data): DepartmentResponse => ({
        ...data,
        results: data.results.map(departmentFromDto),
      }),
    },
  );
}

export function useUserEmploymentChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<EmploymentResponseDto, EmploymentResponse>(
    'users/employment/',
    {
      queryParams,
      enabled,
      initialData: { results: [] },
      select: (data): EmploymentResponse => ({
        ...data,
        results: data.results.map(employmentFromDto),
      }),
    },
  );
}
